import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './StateWiseEmployeeMonitoring.css';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { getDistrict, getStates } from '../../../services/monthlyAttendanceService';
import { capitalizeFirstLetter } from '../../../utils/validations';
import {
    getEmployeeMonitoring,
    getAllStateWiseEmployeesTracking,
    getStateWiseEmployeeMonitoring
} from '../../../services/employeePostingService';

const PAGE_SIZE = 10;

// Tab definitions — status maps to the API "status" query param
const TABS = [
    { key: 'All', label: 'Total Employees', className: 'tab-card--all' },
    { key: 'Present', label: 'Total Present', className: 'tab-card--present' },
    { key: 'Absent', label: 'Total Absent', className: 'tab-card--absent' },
    { key: 'PendingApproval', label: 'Pending Approval  Employees', className: 'tab-card--pending' },
    { key: 'WithDeviation', label: 'Away From Working Location', className: 'tab-card--away' },
    { key: 'WithoutDeviation', label: 'In Working Location', className: 'tab-card--stay' },
];

const StateWiseEmployeeMonitoring = () => {
    const currentYear = new Date().getFullYear();
    const today = new Date();

    const [state, setState] = useState(3);
    const [district, setDistrict] = useState(null);
    const [date, setDate] = useState(today);
    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [districtId, setDistrictId] = useState('');

    const [attendanceData, setAttendanceData] = useState([]);
    const [employeeMonitoringData, setEmployeeMonitoringData] = useState({});

    // ⬇️ NEW: view + tab + pagination state
    const [view, setView] = useState('summary');
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [activeTab, setActiveTab] = useState('Present'); // default tab
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    const effectRan = useRef(false);
    const [isFocused, setIsFocused] = useState({ state: false, district: false, date: false });
    const [errors, setErrors] = useState({});
    const yearInputRef = useRef(null);

    const isFetchDataEnabled = state && date;

    // Fetch states
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const data = await getStates();
                setStateOptions(
                    data.map(item => ({
                        value: item.id,
                        label: capitalizeFirstLetter(item.stateName),
                    }))
                );
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };
        if (!effectRan.current) fetchStates();
        return () => { effectRan.current = true; };
    }, []);

    // Fetch districts
    useEffect(() => {
        if (state) {
            const fetchDistricts = async () => {
                try {
                    const data = await getDistrict(state);
                    let districts = [];
                    if (Array.isArray(data)) districts = data;
                    else if (Array.isArray(data?.districts)) districts = data.districts;
                    else if (Array.isArray(data?.data)) districts = data.data;
                    else if (Array.isArray(data?.result)) districts = data.result;

                    const list = districts.map((d) => ({
                        value: d.districtId,
                        label: capitalizeFirstLetter(d.districtName),
                    }));

                    setDistrictOptions([{ value: 'ALL', label: 'All Districts' }, ...list]);
                } catch (error) {
                    console.error('Error fetching districts:', error);
                    setDistrictOptions([{ value: 'ALL', label: 'All Districts' }]);
                }
            };
            fetchDistricts();
        } else {
            setDistrictOptions([]);
            setDistrict(null);
            setDistrictId('');
        }
    }, [state]);

    // Reset data on filter change
    useEffect(() => {
        setAttendanceData([]);
        setEmployeeMonitoringData({});
        setView('summary');
        setSelectedDistrict(null);
        setActiveTab('Present');
        setPage(0);
    }, [state, district, date]);

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                break;
            case 'district':
                if (value === 'ALL') {
                    setDistrict('ALL');
                    setDistrictId('');
                } else {
                    setDistrict(value);
                    setDistrictId(value);
                }
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleYearChange = (date) => {
        setDate(date);
        setErrors(prev => ({ ...prev, date: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!state) newErrors.state = 'State is required';
        if (!date) newErrors.date = 'Year is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // -------- Fetch summary (tab counts) --------
    const fetchReport = async () => {
        if (!validateForm()) return;
        try {
            const data = await getStateWiseEmployeeMonitoring(state, district, date);
            setEmployeeMonitoringData(data || {});
            setView('summary');
            setSelectedDistrict(null);

            // 👇 on load, fetch the "Present" tab data
            fetchTabData('Present', 0);
        } catch (error) {
            console.error('Error fetching report data:', error);
            setErrors(prev => ({ ...prev, general: 'Failed to fetch report data' }));
            alert('Failed to fetch report data. Please try again.');
        }
    };

    // -------- Fetch data for a tab (paginated) --------
    const fetchTabData = async (status, pageNumber = 0) => {
        if (!validateForm()) return;

        setLoadingDetail(true);
        setActiveTab(status);
        setPage(pageNumber);

        try {
            const response = await getAllStateWiseEmployeesTracking(
                state,
                districtId,
                date,
                status,
                pageNumber,
                PAGE_SIZE
            );

            console.log('TAB DATA:', status, response);

            // Handle multiple possible shapes
            const content = response?.content || response?.data?.content || response || [];
            const total = response?.totalElements ?? response?.data?.totalElements ?? content.length;
            const pages = response?.totalPages ?? response?.data?.totalPages ?? Math.ceil(total / PAGE_SIZE);

            setAttendanceData(content);
            setTotalRecords(total);
            setTotalPages(pages);
        } catch (error) {
            console.error('Error fetching tab data:', error);
            setAttendanceData([]);
            setTotalRecords(0);
            setTotalPages(0);
        } finally {
            setLoadingDetail(false);
        }
    };

    useEffect(() => { fetchReport(); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        await fetchReport();
    };

    // Tab click handler
    const handleTabClick = (status) => {
        fetchTabData(status, 0); // reset to first page
    };

    // Pagination handlers
    const handlePrevPage = () => {
        if (page > 0) fetchTabData(activeTab, page - 1);
    };
    const handleNextPage = () => {
        if (page + 1 < totalPages) fetchTabData(activeTab, page + 1);
    };
    const handlePageClick = (p) => fetchTabData(activeTab, p);

    const minDate = new Date(2000, 0, 1);
    const maxDate = new Date(currentYear, 11, 31);

    // Helper to get tab count from summary
    const getTabCount = (statusKey) => {
        switch (statusKey) {
            
            case 'All': return employeeMonitoringData?.totalEmployees ?? 0;
            case 'Present': return employeeMonitoringData?.totalPresent ?? 0;
            case 'Absent': return employeeMonitoringData?.totalAbsent ?? 0;
            case 'PendingApproval': return employeeMonitoringData?.pendingApprovalCount ?? 0;
            case 'WithDeviation': return employeeMonitoringData?.flaggedEmployees ?? 0;
            case 'WithoutDeviation': return employeeMonitoringData?.noDeviationCount ?? 0;
            default: return 0;
        }
    };

    // Build pagination page numbers
    const pageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(0, page - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible);
        if (end - start < maxVisible) start = Math.max(0, end - maxVisible);
        for (let i = start; i < end; i++) pages.push(i);
        return pages;
    };

    return (
        <div className="monitoring-reportrarel-container">
            <div className="monitoring-reportrarel-box">
                <h2 className="monitoring-reportrarel-heading">Employee Location Tracking System</h2>
                {errors.general && <div className="error-message">{errors.general}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="monitoring-reportrarel-maincontainer">
                        <div className="monitoring-reportrarel-container-part">
                            <div className={`monitoring-reportrarel-floating-label-container ${isFocused.state || state ? 'float-label' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === state) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>State</label>
                                {errors.state && <div className="error-message">{errors.state}</div>}
                            </div>
                        </div>
                        <div className="monitoring-reportrarel-container-part">
                            <div className={`monitoring-reportrarel-floating-label-container ${isFocused.district || district ? 'float-label' : ''}`}>
                                <Select
                                    name="district"
                                    options={districtOptions}
                                    value={districtOptions.find(opt => opt.value === district) || null}
                                    onChange={option => handleInputChange(option, 'district')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label>District</label>
                            </div>
                        </div>
                    </div>

                    <div className="monitoring-reportrarel-maincontainer">
                        <div className="monitoring-reportrarel-container-part">
                            <div className={`monitoring-reportrarel-floating-label-container ${isFocused.date || date ? 'float-label' : ''}`}>
                                <DatePicker
                                    selected={date}
                                    onChange={(date) => handleYearChange(date)}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, date: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, date: false }))}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText=""
                                    className="monitoring-reportrarel-date-picker"
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    ref={yearInputRef}
                                    title="Select a date"
                                />
                                <label><span className="required">*</span>Date</label>
                                {errors.date && <div className="error-message">{errors.date}</div>}
                            </div>
                        </div>
                        <div className="monitoring-reportrarel-container-part"></div>
                    </div>

                    <div className="monitoring-reportrarel-btn-div">
                        <button type="submit" className="monitoring-reportrarel-btn" disabled={!isFetchDataEnabled}>
                            Generate Report
                        </button>
                    </div>
                </form>
            </div>

            {/* ============= TABS (clickable) ============= */}
            <div className="tab-grids">
                {TABS.map(tab => (
                    <div
                        key={tab.key}
                        className={`tab-card ${tab.className} ${activeTab === tab.key ? 'tab-card--active' : ''}`}
                        onClick={() => handleTabClick(tab.key)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleTabClick(tab.key)}
                    >
                        <div className="tab-card__label">{tab.label}</div>
                        <div className="tab-card__value">{getTabCount(tab.key)}</div>
                    </div>
                ))}
            </div>

            {/* ============= DETAIL TABLE ============= */}
            <div className="reportrarel-table-monthlt-att-container">
                <table className="reportrarel-table-monthlt-att">
                    <thead>
                        <tr>
                            <th rowSpan="3" className="sticky-col sno-col">S.No</th>
                            <th rowSpan="3" className="sticky-col name-col">Employee Name</th>
                            <th rowSpan="3" className="sticky-col name-col">Designation</th>
                            <th rowSpan="3" className="sticky-col loc-col">Location</th>
                            <th rowSpan="3" className="sticky-col loc-col">Attendance Status</th>
                            <th colSpan="48" className="deviation-header">Deviation From Job Location</th>
                        </tr>
                        <tr>
                            {Array.from({ length: 24 }, (_, i) => (
                                <th key={i + 1} colSpan="2" className="hour-header">
                                    {String(i).padStart(2, '0')}:00 – {String(i + 1).padStart(2, '0')}:00 hr
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {Array.from({ length: 24 }, (_, i) => (
                                <React.Fragment key={`sub-${i}`}>
                                    <th className="sub-header">00 – 30 min</th>
                                    <th className="sub-header">30 – 60 min</th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loadingDetail ? (
                            <tr>
                                <td colSpan="54" className="loading-cell">
                                    Loading employees…
                                </td>
                            </tr>
                        ) : attendanceData.length > 0 ? (
                            attendanceData.map((employee, empIdx) => (
                                <tr key={employee.employeeUniqueId || empIdx}>
                                    <td className="sticky-col sno-col">{page * PAGE_SIZE + empIdx + 1}</td>
                                    <td className="sticky-col name-col">{employee.employeeName}</td>
                                    <td className="sticky-col name-col">{employee.designation}</td>
                                    <td className="sticky-col loc-col">{employee.locationName}</td>
                                    <td className="sticky-col loc-col">{employee.attendanceStatus}</td>

                                    {employee.timeline?.map((hourBlock) =>
                                        hourBlock.slots?.map((slot) => {
                                            const firstRecord = slot.records?.[0];
                                            const rawValue = firstRecord?.distanceFromPreviousMeters;
                                            return (
                                                <td
                                                    key={slot.timeWindow}
                                                    className={`distance-cell ${rawValue ? 'has-distance' : ''}`}
                                                    title={`${slot.timeWindow} | Records: ${slot.records?.length || 0} | Distance: ${rawValue ?? 0}`}
                                                >
                                                    {rawValue !== undefined && rawValue !== null ? `${rawValue} km` : '-'}
                                                </td>
                                            );
                                        })
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="54" className="no-data-cell">
                                    No employee data available.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ============= PAGINATION ============= */}
            {attendanceData.length > 0 && (
                <div className="pagination-container">
                    <div className="pagination-info">
                        Showing <strong>{page * PAGE_SIZE + 1}</strong> –{' '}
                        <strong>{Math.min((page + 1) * PAGE_SIZE, totalRecords)}</strong> of{' '}
                        <strong>{totalRecords}</strong>
                    </div>
                    <div className="pagination-controls">
                        <button
                            className="pagination-btn"
                            onClick={handlePrevPage}
                            disabled={page === 0 || loadingDetail}
                        >
                            ‹ Prev
                        </button>

                        {pageNumbers().map((p) => (
                            <button
                                key={p}
                                className={`pagination-btn ${p === page ? 'pagination-btn--active' : ''}`}
                                onClick={() => handlePageClick(p)}
                                disabled={loadingDetail}
                            >
                                {p + 1}
                            </button>
                        ))}

                        <button
                            className="pagination-btn"
                            onClick={handleNextPage}
                            disabled={page + 1 >= totalPages || loadingDetail}
                        >
                            Next ›
                        </button>
                    </div>
                </div>
            )}

            
        </div>
    );
};

export default StateWiseEmployeeMonitoring;